import React, { useEffect, useState } from "react";
import { mfaAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Copy, Download, Loader2, LogOut, MonitorSmartphone, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const errText = (e, fallback) => {
    const d = e?.response?.data?.detail;
    return typeof d === "string" ? d : fallback;
};
const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");
const browserName = (ua) => {
    const u = ua || "";
    const b = /Edg\//.test(u) ? "Edge" : /Chrome\//.test(u) ? "Chrome" : /Safari\//.test(u) ? "Safari" : /Firefox\//.test(u) ? "Firefox" : "Browser";
    const os = /iPhone|iPad/.test(u) ? "iPhone/iPad" : /Android/.test(u) ? "Android" : /Mac OS X/.test(u) ? "Mac" : /Windows/.test(u) ? "Windows" : "";
    return os ? `${b} on ${os}` : b;
};

// Shown once after setup / renewal: they can't be seen again.
export const RecoveryCodes = ({ codes, onDone }) => {
    const text = `Boutiqaat SRM — recovery codes (each works once)\n\n${codes.join("\n")}\n`;
    const download = () => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
        a.download = "boutiqaat-recovery-codes.txt";
        a.click();
        URL.revokeObjectURL(a.href);
    };
    return (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
                Keep these somewhere safe (not on the same phone). If you lose your phone, each code signs you in once. They won't be shown again.
            </p>
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-border/40 bg-background p-4 font-mono text-sm">
                {codes.map((c) => (
                    <span key={c}>{c}</span>
                ))}
            </div>
            <div className="flex flex-wrap gap-2">
                <Button
                    variant="outline"
                    onClick={() =>
                        navigator.clipboard
                            .writeText(text)
                            .then(() => toast.success("Copied"))
                            .catch(() => toast.error("Couldn't copy — use Download"))
                    }
                >
                    <Copy /> Copy
                </Button>
                <Button variant="outline" onClick={download}>
                    <Download /> Download
                </Button>
                {onDone && (
                    <Button className="ml-auto" onClick={onDone}>
                        I've saved them
                    </Button>
                )}
            </div>
        </div>
    );
};

// Scan → type a code → recovery codes. Used on the forced setup screen and on Profile.
export const TwoFactorEnroll = ({ onDone }) => {
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [codes, setCodes] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        mfaAPI
            .setup()
            .then((r) => setSetup(r.data))
            .catch((e) => toast.error(errText(e, "Couldn't start the setup")));
    }, []);

    const enable = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            setCodes((await mfaAPI.enable(code.trim())).data.recovery_codes);
            toast.success("Two-factor sign-in is on");
        } catch (err) {
            toast.error(errText(err, "That code didn't match"));
            setCode("");
        } finally {
            setBusy(false);
        }
    };

    if (codes) return <RecoveryCodes codes={codes} onDone={onDone} />;
    if (!setup) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
    return (
        <div className="space-y-5">
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                <li>Install an authenticator app on your phone (Google Authenticator, Microsoft Authenticator or 1Password).</li>
                <li>In the app, add an account and scan this code.</li>
                <li>Type the 6-digit code the app shows.</li>
            </ol>
            <div className="flex flex-wrap items-center gap-5">
                <img src={setup.qr} alt="QR code to scan with your authenticator app" className="h-44 w-44 rounded-lg bg-white p-1" />
                <div className="space-y-1 text-sm">
                    <p className="text-muted-foreground">Can't scan? Enter this key in the app:</p>
                    <p className="select-all font-mono">{setup.secret}</p>
                    <p className="text-xs text-muted-foreground">Account: {setup.issuer} · time-based</p>
                </div>
            </div>
            <form onSubmit={enable} className="flex max-w-sm gap-2">
                <Input
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, "").slice(0, 7))}
                    className="bg-background text-center font-mono tracking-widest"
                />
                <Button type="submit" disabled={busy || code.replace(/\D/g, "").length !== 6}>
                    {busy ? <Loader2 className="animate-spin" /> : "Turn on"}
                </Button>
            </form>
        </div>
    );
};

// Full screen, before anything else opens, for a role that must have it (SRM).
export const TwoFactorSetup = () => {
    const { refreshUser, logout } = useAuth();
    return (
        <div className="flex min-h-screen items-center justify-center bg-background p-6">
            <Card className="w-full max-w-xl border-border/40 bg-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5" strokeWidth={1.5} /> Set up two-factor sign-in
                    </CardTitle>
                    <CardDescription>
                        Your account manages everyone's data, so signing in also needs a code from your phone. It takes a minute; on a device you trust you
                        can skip the code for 30 days.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <TwoFactorEnroll onDone={() => refreshUser()} />
                    <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={logout}>
                        <LogOut className="h-3 w-3" /> Sign out
                    </button>
                </CardContent>
            </Card>
        </div>
    );
};

// Profile page card: status, recovery codes, remembered devices.
export const TwoFactorCard = () => {
    const { refreshUser } = useAuth();
    const [st, setSt] = useState(null);
    const [mode, setMode] = useState(null); // "enroll" | "renew" | "disable"
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [codes, setCodes] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () =>
        mfaAPI
            .status()
            .then((r) => setSt(r.data))
            .catch(() => setSt(null));
    useEffect(() => {
        load();
    }, []);

    const run = async (fn, ok) => {
        setBusy(true);
        try {
            const r = await fn();
            if (ok) toast.success(ok);
            return r;
        } catch (e) {
            toast.error(errText(e, "That didn't work"));
            return null;
        } finally {
            setBusy(false);
            setCode("");
            load();
        }
    };

    if (!st) return null;
    return (
        <Card className="border-border/40 bg-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5" strokeWidth={1.5} /> Two-factor sign-in
                </CardTitle>
                <CardDescription>
                    {st.enabled
                        ? `On. Signing in asks for a code from your authenticator app, except on devices you chose to remember (${st.device_days} days).`
                        : "Off. Turn it on so a stolen password isn't enough to get into your account."}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
                {codes ? (
                    <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
                ) : !st.enabled ? (
                    mode === "enroll" ? (
                        <TwoFactorEnroll
                            onDone={() => {
                                setMode(null);
                                load();
                                refreshUser();
                            }}
                        />
                    ) : (
                        <Button onClick={() => setMode("enroll")}>Turn on</Button>
                    )
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                            <span className={st.recovery_left <= 3 ? "text-amber-300" : "text-muted-foreground"}>
                                {st.recovery_left} recovery code{st.recovery_left === 1 ? "" : "s"} left
                            </span>
                            <Button size="sm" variant="outline" onClick={() => setMode(mode === "renew" ? null : "renew")}>
                                New recovery codes
                            </Button>
                            {!st.required && (
                                <Button size="sm" variant="ghost" onClick={() => setMode(mode === "disable" ? null : "disable")}>
                                    Turn off
                                </Button>
                            )}
                        </div>
                        {mode === "renew" && (
                            <form
                                className="flex max-w-sm gap-2"
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    const r = await run(() => mfaAPI.newRecoveryCodes(code.trim()), "New codes made — the old ones no longer work");
                                    if (r) {
                                        setCodes(r.data.recovery_codes);
                                        setMode(null);
                                    }
                                }}
                            >
                                <Input placeholder="Code from the app" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} className="bg-background" />
                                <Button type="submit" disabled={busy}>
                                    Make new codes
                                </Button>
                            </form>
                        )}
                        {mode === "disable" && (
                            <form
                                className="grid max-w-md gap-2 sm:grid-cols-[1fr_1fr_auto]"
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    if (await run(() => mfaAPI.disable(password, code.trim()), "Two-factor sign-in is off")) {
                                        setMode(null);
                                        setPassword("");
                                        refreshUser();
                                    }
                                }}
                            >
                                <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-background" />
                                <Input placeholder="Code from the app" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} className="bg-background" />
                                <Button type="submit" variant="destructive" disabled={busy}>
                                    Turn off
                                </Button>
                            </form>
                        )}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <p className="flex items-center gap-2 text-sm font-medium">
                                    <MonitorSmartphone className="h-4 w-4" strokeWidth={1.5} /> Remembered devices
                                </p>
                                {st.devices.length > 1 && (
                                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => mfaAPI.forgetDevice("all"), "Forgot every device")}>
                                        Forget all
                                    </Button>
                                )}
                            </div>
                            {st.devices.length === 0 ? (
                                <p className="text-sm text-muted-foreground">None — every sign-in asks for a code.</p>
                            ) : (
                                st.devices.map((d) => (
                                    <div key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-border/30 px-3 py-2 text-sm">
                                        <div>
                                            <p>{browserName(d.ua)}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {d.ip} · since {when(d.created_at)} · until {when(d.expires_at)}
                                            </p>
                                        </div>
                                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => mfaAPI.forgetDevice(d.id), "Forgotten")}>
                                            <Trash2 /> Forget
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};
